/* eslint-disable import/no-named-as-default */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import Accordion from 'yoda-core-components/lib/components/Accordion';
import OldAccordion from 'yoda-core-components/lib/components/Accordion/Accordion/Accordion';
import GalleryAccordionSection from './components/GalleryAccordionSection/GalleryAccordionSection';
import * as styles from './FilterPanel.css';
import FilterCategory from '../FilterCategory/FilterCategory';
import SelectedFacets from '../SelectedFacets/SelectedFacets';
import StoreFilterCategory from '../StoreFilterCategory/StoreFilterCategory';
import updateMultiSelectFacetInfo from '../../actions/MultiSelectFilterActions';
import { getPageType, getURLParameterBykey, updateUrl } from '../../utils';

const cx = classNames.bind(styles);

export const FilterPanel = () => {
    const {
        context: {
            deviceType: { isDesktop = true, isMobile = false, isTablet = false } = {},
            featureFlags: {
                enableDesktopViewForTabletLandscapeOrientation: enableDesktopViewForTablet = false,
                enableMultiSelectFilters = false,
                enableStickyFacet = false,
                enableBrandNameCasing = false,
            } = {},
        } = {},
        countOfSearchResults = 0,
        filters: { filterList = [] } = {},
        multiSelectFilter: { multiFacetUrl = '', multiSelectedFacetsList = {} } = {},
        sliderStatus = false,
    } = useSelector((store) => store);

    const dispatch = useDispatch();

    const hasFilters = filterList && filterList.length > 0;
    const isTabletView = isTablet && enableDesktopViewForTablet && !sliderStatus;
    const isDesktopView = isDesktop || isTabletView;
    const [expandOnSearch, setExpandOnSearch] = useState(false);

    useEffect(() => {
        getPageType(window.location.href) === 's' &&
            setExpandOnSearch(
                filterList
                    .map(({ facetValueList = [] }) => facetValueList)
                    .reduce((previous, current) => [...previous, ...current], [])
                    .filter(
                        ({ solrFacetName = '' }) =>
                            !!getURLParameterBykey(solrFacetName, window.location.href, false)
                                .length
                    ).length
            );
    }, [filterList]);

    if (!hasFilters && countOfSearchResults === 1) return <>1 Result</>;

    const facets = filterList.map((facet) => {
        const {
            activeFacet = false,
            facetName = '',
            facetValueList = [],
            noOfSelectedFacetValues = 0,
        } = facet;

        const hasSelected = facetValueList.filter(({ selected }) => selected).length > 0;
        const selectedCount = noOfSelectedFacetValues;
        let header = facetName;
        let expanded = false;

        if (expandOnSearch && (hasSelected || activeFacet)) expanded = true;
        else if (enableMultiSelectFilters) expanded = activeFacet;
        else expanded = hasSelected || activeFacet;

        if (selectedCount > 0) header += ` (${selectedCount})`;

        return {
            expanded,
            header,
            panel: <FilterCategory dataSource={facet} />,
        };
    });

    /* istanbul ignore next */
    const accordionTitleClicked = (event) => {
        const isExpanded = event.target.closest('button[aria-expanded]').ariaExpanded;
        if (
            isExpanded === 'false' &&
            enableMultiSelectFilters &&
            multiSelectedFacetsList &&
            Object.keys(multiSelectedFacetsList).length > 0 &&
            multiFacetUrl
        ) {
            dispatch(
                updateMultiSelectFacetInfo({
                    enableProductsLoader: true,
                    enableFacetLoader: true,
                    scrollToTitle: false,
                })
            );
            multiFacetUrl && updateUrl(multiFacetUrl, null, null, null);
        }
    };

    return hasFilters ? (
        <div
            className={[
                'bg-white-100',
                'font-open-sans-regular',
                'h-full',
                cx({ lowercase: !enableBrandNameCasing }),
                'scrolling-touch',
                'text-gray-600',
                'text-small',
                cx('wrapper', {
                    desktop: isDesktopView && !isTabletView,
                    tablet: isTabletView,
                    'mr-2': isDesktopView,
                    'overflow-y-scroll': sliderStatus,
                }),
            ].join(' ')}
        >
            <SelectedFacets
                {...{
                    sliderStatus,
                    wrapperClass: cx('selectedFacets'),
                }}
            />
            <StoreFilterCategory
                {...{
                    customWrapperClasses: [
                        'border-b-1 border-gray-200 border-solid border-t-1',
                        'py-2',
                        cx({ storeCustomClassTablet: isTabletView }),
                    ].join(' '),
                    inSlider: !isDesktop,
                    sliderStatus,
                }}
            />
            {isMobile && enableMultiSelectFilters ? (
                <Accordion
                    automationId="filterAccordian"
                    className={cx({
                        'filter-panel-container': enableStickyFacet && isDesktopView,
                        'overflow-y-auto': enableStickyFacet && isDesktopView,
                    })}
                    onClick={accordionTitleClicked}
                    panelLeftIndent={false}
                    sectionUnderline
                    toggleControlLeft
                    toggleOnlyOne={enableMultiSelectFilters}
                >
                    {facets}
                </Accordion>
            ) : (
                <OldAccordion
                    {...{
                        automationId: 'filterAccordian',
                        isAutoCollapsible: false,
                        themeConfig: {
                            accordionActiveClass: styles.selected,
                            accordionContainerClass: [
                                'border-b-0',
                                cx({
                                    'filter-panel-container': enableStickyFacet && isDesktopView,
                                    'overflow-y-auto': enableStickyFacet && isDesktopView,
                                }),
                            ].join(' '),
                            accordionIconClass: 'mr-1 order-1',
                            accordionPanelClass: [
                                'overflow-y-hidden',
                                'relative',
                                cx('filter-panel'),
                            ].join(' '),
                            accordionSectionClass: [
                                'border-b-1 border-solid border-gray-200',
                                'flex flex-col',
                                'py-2',
                            ].join(' '),
                            accordionTitleClass: [
                                'flex',
                                'font-open-sans-bold',
                                'items-center',
                                'pl-half',
                                'text-medium',
                            ].join(' '),
                            accordionTitleTextClass: 'order-2',
                        },
                    }}
                >
                    {filterList.map((facetDetails, filterIndex) => (
                        <GalleryAccordionSection
                            {...{
                                facetDetails,
                                filterIndex,
                                key: facetDetails.facetId || facetDetails.facetName,
                            }}
                        />
                    ))}
                </OldAccordion>
            )}
        </div>
    ) : null;
};

export default FilterPanel;
